import { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Gift, Handshake, Send, CheckCircle } from 'lucide-react';
import { contatoAPI } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

export default function Apoie() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: '',
    tipo: 'apoiador'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await contatoAPI.enviar(formData);
      setSuccess(true);
      setFormData({ nome: '', email: '', telefone: '', mensagem: '', tipo: 'apoiador' });
      toast({
        title: "Mensagem enviada!",
        description: "Entraremos em contato em breve.",
      });
    } catch (error) {
      console.error('Erro ao enviar:', error);
      toast({
        title: "Erro ao enviar",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const beneficios = [
    { icon: Heart, title: 'Transforme Vidas', desc: 'Ajude a formar atletas e cidadãs' },
    { icon: Users, title: 'Fortaleça a Comunidade', desc: 'Invista no esporte local' },
    { icon: Gift, title: 'Receba Reconhecimento', desc: 'Seu nome em nossos materiais' },
    { icon: Handshake, title: 'Faça Parte da Família', desc: 'Acesso exclusivo a eventos' },
  ];

  return (
    <div data-testid="apoie-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 hero-pattern" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Heart className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase">
              Seja um <span className="text-yellow-400">Apoiador</span>
            </h1>
            <p className="text-xl text-slate-300 mt-4 max-w-2xl mx-auto">
              Sua contribuição transforma sonhos em realidade. Apoie o projeto ACEC Feminino!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefícios */}
      <section className="py-16 bg-yellow-400" data-testid="beneficios-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {beneficios.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/20 backdrop-blur-sm rounded-xl p-6 text-center"
              >
                <item.icon className="w-10 h-10 mx-auto text-blue-900 mb-3" />
                <h3 className="font-heading text-lg font-bold text-blue-900 uppercase">
                  {item.title}
                </h3>
                <p className="text-blue-800 text-sm mt-1">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulário */}
      <section className="py-24 bg-white" data-testid="form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-slate-900 uppercase">
              Entre em <span className="text-blue-700">Contato</span>
            </h2>
            <p className="text-slate-600 mt-4">
              Preencha o formulário abaixo e nossa equipe entrará em contato para apresentar as formas de apoio disponíveis.
            </p>
          </div>

          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Mensagem Enviada!
              </h3>
              <p className="text-slate-600 mb-6">
                Obrigado pelo seu interesse. Entraremos em contato em breve!
              </p>
              <button
                onClick={() => setSuccess(false)}
                data-testid="send-another"
                className="text-blue-700 font-bold hover:text-blue-800"
              >
                Enviar outra mensagem
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="apoie-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    data-testid="input-nome"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 transition-all"
                    placeholder="Seu nome"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="input-email"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 transition-all"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Telefone
                </label>
                <input
                  type="tel"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  data-testid="input-telefone"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 transition-all"
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Tipo de Interesse *
                </label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  data-testid="select-tipo"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 transition-all"
                >
                  <option value="apoiador">Quero ser Apoiador/Patrocinador</option>
                  <option value="voluntario">Quero ser Voluntário</option>
                  <option value="parceria">Proposta de Parceria</option>
                  <option value="outro">Outro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Mensagem *
                </label>
                <textarea
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="input-mensagem"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                  placeholder="Conte-nos mais sobre como gostaria de contribuir..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-apoie"
                className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-full px-8 py-4 font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar Mensagem
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Patrocinadores Atuais */}
      <section className="py-16 bg-slate-50" data-testid="patrocinadores-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-8">
            Nossos Apoiadores
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-slate-600 font-medium">PAREX</p>
              <p className="text-slate-400 text-sm">Materiais de Escritório</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-slate-600 font-medium">PAVIBRA ENGENHARIA</p>
              <p className="text-slate-400 text-sm">22 Bolas Mikasa</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
